import React, { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { DatePicker, LocalizationProvider } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import RemoveShoppingCartIcon from "@mui/icons-material/RemoveShoppingCart";
import UndoIcon from "@mui/icons-material/Undo";
import moment from "moment";

import Loader from "@jumbo/components/Loader";
import { Table } from "app/components";
import DialogConfirm from "@jumbo/components/JumboDialog/components/DialogConfirm";
import { useDisclosure } from "@jumbo/components/JumboDialog/hooks/useDisclosure";

const BLOCKER_LABEL = {
  noVariant: "No variant in this size chart",
  ambiguous: "More than one variant matches",
  unmapped: "SKU not mapped",
  excluded: "Size belongs to no size chart",
  platform: "Platform not supported",
};

const list = (values) => (values ?? []).join(", ") || "-";

const text = (value) => String(value ?? "");

const NUMERIC_SORT = ["quantity", "currentStock", "stockAfter"];

/**
 * A stock figure, coloured by whether it is below zero.
 *
 * `plain` leaves a healthy figure uncoloured - used for the balance before a
 * stock out, where only a figure that is already negative needs pointing out.
 */
const StockValue = ({ value, plain = false }) => {
  const negative = (value ?? 0) < 0;
  return (
    <Box
      component="span"
      sx={{
        fontWeight: "bold",
        color: negative
          ? "error.main"
          : plain
          ? "text.primary"
          : "success.main",
      }}
    >
      {value ?? 0}
    </Box>
  );
};

/**
 * Sorts the rows a stock out table shows.
 *
 * Both tables hold every row they have - there is no paging here - so the
 * order is worked out in the browser rather than asked of the API.
 *
 * The variant column prints the product under the variant code, so sorting it
 * orders by product first and keeps each product's variants together.
 */
const sortRows = (rows, order, orderBy) => {
  if (!orderBy) return rows;
  const direction = order === "asc" ? 1 : -1;

  const byVariant = (a, b) =>
    text(a.productName).localeCompare(text(b.productName)) ||
    text(a.variantCode).localeCompare(text(b.variantCode));

  return [...rows].sort((a, b) => {
    if (orderBy === "outDate") {
      return (
        (new Date(a.outDate || 0) - new Date(b.outDate || 0)) * direction ||
        byVariant(a, b)
      );
    }
    if (NUMERIC_SORT.includes(orderBy)) {
      // Ties fall back to the variant so the order never wobbles between
      // renders for rows with the same figure.
      return (
        ((a[orderBy] ?? 0) - (b[orderBy] ?? 0)) * direction || byVariant(a, b)
      );
    }
    return byVariant(a, b) * direction;
  });
};

/**
 * Sort state for one table. The shared table owns the header arrows and hands
 * the choice back here, which is where the rows are actually reordered.
 */
const useRowSort = (rows) => {
  // Matches the order the API already returns, so the first view of the table
  // is what the arrow says it is.
  const [sort, setSort] = useState({ order: "desc", orderBy: "quantity" });

  // Same choice back again keeps the old object, so React can skip the render
  // the shared table would otherwise cause on mount.
  const onSort = useCallback(
    (order, orderBy) =>
      setSort((prev) =>
        prev.order === order && prev.orderBy === orderBy
          ? prev
          : { order, orderBy }
      ),
    []
  );

  const sorted = useMemo(
    () => sortRows(rows, sort.order, sort.orderBy),
    [rows, sort]
  );

  return { ...sort, onSort, sorted };
};

// A merged row can carry a long SKU list; it wraps inside its own cell rather
// than stretching the table sideways.
const wrapCell = {
  maxWidth: 340,
  whiteSpace: "normal",
  wordBreak: "break-word",
};

const caption = { display: "block", lineHeight: 1.3 };

/**
 * The rows a stock out would write, or did write.
 *
 * `remaining` and `records` carry the same fields, so one set of columns
 * renders both. `onRevert` is passed only for the records - those are the
 * ones that can be put back.
 */
const variantColumns = ({ onRevert, revertDisabled, stock }) => [
  {
    id: "variantCode",
    label: "Variant",
    sort: true,
    minWidth: 170,
    render: (row) => (
      <>
        {row.variantCode || row.variantName || "-"}
        {row.productName && (
          <Typography variant="caption" color="text.secondary" sx={caption}>
            {row.productName}
          </Typography>
        )}
      </>
    ),
  },
  {
    id: "variantSize",
    label: "Variant Size",
    render: (row) => row.variantSize || "-",
  },
  {
    id: "sizeChart",
    label: "Size Chart",
    render: (row) => (
      <Chip size="small" variant="outlined" label={row.sizeChart} />
    ),
  },
  {
    id: "printedSizes",
    label: "Label Sizes",
    // What the labels actually printed - this is what the size chart matched
    // to the variant's own size.
    render: (row) => list(row.printedSizes),
  },
  {
    id: "skus",
    label: "SKUs",
    render: (row) => <Box sx={wrapCell}>{list(row.skus)}</Box>,
  },
  {
    id: "quantity",
    label: "Qty Out",
    sort: true,
    align: "center",
    isTotal: true,
    render: (row) => <b>{row.quantity}</b>,
  },
  /**
   * Before a stock out the useful figure is where the balance ends up; after
   * one, the balance already includes it, so the current figure is the answer.
   * Red means below zero. Nothing is stopped by it - a negative balance is
   * allowed, the same as a manual Out in the app.
   */
  ...(stock === "after"
    ? [
        {
          id: "currentStock",
          label: "Current Stock",
          sort: true,
          align: "center",
          render: (row) => <StockValue value={row.currentStock} plain />,
        },
        {
          id: "stockAfter",
          label: "After Stock Out",
          sort: true,
          align: "center",
          render: (row) => <StockValue value={row.stockAfter} />,
        },
      ]
    : []),
  ...(stock === "current"
    ? [
        {
          // The day this variant left stock - variants taken out later with
          // "Stock Out Remaining" can carry a different day.
          id: "outDate",
          label: "Stock Out Date",
          sort: true,
          align: "center",
          render: (row) =>
            row.outDate ? moment(row.outDate).format("DD/MM/YYYY") : "-",
        },
        {
          id: "currentStock",
          label: "Current Stock",
          sort: true,
          align: "center",
          render: (row) => <StockValue value={row.currentStock} />,
        },
      ]
    : []),
  ...(onRevert
    ? [
        {
          id: "revert",
          label: "Revert",
          align: "center",
          render: (row) => (
            // Puts this one variant's stock back without touching the rest of
            // the import.
            <Tooltip
              title={
                revertDisabled
                  ? "You do not have permission to revert a stock out."
                  : `Put back ${row.quantity} pc(s) to ${row.variantCode}`
              }
            >
              <span>
                <IconButton
                  size="small"
                  color="warning"
                  disabled={revertDisabled}
                  onClick={() => onRevert(row)}
                >
                  <UndoIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ),
        },
      ]
    : []),
];

const blockerColumns = [
  {
    id: "type",
    label: "Problem",
    minWidth: 170,
    render: (row) => (
      <Box component="span" sx={{ color: "error.main", fontWeight: "bold" }}>
        {BLOCKER_LABEL[row.type] ?? row.type}
      </Box>
    ),
  },
  {
    id: "productName",
    label: "Product / SKU",
    render: (row) => (
      <Box sx={wrapCell}>
        {row.productName ?? "-"}
        {row.skus?.length ? (
          <Typography variant="caption" color="text.secondary" sx={caption}>
            {list(row.skus)}
          </Typography>
        ) : null}
      </Box>
    ),
  },
  {
    id: "sizeChart",
    label: "Size Chart",
    render: (row) =>
      row.sizeChart ? (
        <Chip size="small" variant="outlined" label={row.sizeChart} />
      ) : (
        "-"
      ),
  },
  {
    id: "printedSizes",
    label: "Label Sizes",
    render: (row) => list(row.printedSizes ?? row.sizes),
  },
  {
    id: "quantity",
    label: "Orders",
    align: "center",
    render: (row) => row.quantity ?? "-",
  },
  {
    id: "message",
    label: "What to do",
    minWidth: 320,
    render: (row) => (
      <Box sx={{ maxWidth: 440, whiteSpace: "normal" }}>
        {row.message}
        {row.availableSizes?.length ? (
          <Typography variant="caption" color="text.secondary" sx={caption}>
            This product has sizes: {list(row.availableSizes)}
          </Typography>
        ) : null}
        {row.variantCodes?.length ? (
          <Typography variant="caption" color="text.secondary" sx={caption}>
            Matched: {list(row.variantCodes)}
          </Typography>
        ) : null}
      </Box>
    ),
  },
];

/**
 * Takes a saved import's quantities out of app inventory, and shows exactly
 * which variant loses what before anything is done.
 *
 * Nothing goes out while a single cell is unresolved: a half-applied stock out
 * would be far harder to unpick than one that never ran.
 */
const StockOutPanel = ({
  stockOut,
  canStockOut = false,
  onStockOut,
  onUndo,
  onRevert,
}) => {
  const confirmOut = useDisclosure();
  const confirmUndo = useDisclosure();
  // Carries the row being reverted, so the dialog can name it.
  const confirmRevert = useDisclosure();
  // The day the stock leaves. The drawer unmounts this panel when it closes,
  // so every opening starts from today.
  const [outDate, setOutDate] = useState(() => new Date());
  const outDateValid =
    moment(outDate).isValid() && !moment(outDate).isAfter(moment(), "day");

  const { loading, working, data } = stockOut ?? {};

  // Read before the guards below, because the sort hooks have to run on every
  // render whether or not there is anything to show yet.
  const records = data?.records ?? [];
  const remaining = data?.remaining ?? [];

  const recordSort = useRowSort(records);
  const remainingSort = useRowSort(remaining);

  if (loading && !data) return <Loader />;
  if (!data) return null;

  const { status, stockOutAt, stockOutBy, blockers, totals } = data;

  /**
   * A variant can be reverted on its own, so an import is not simply done or
   * not: some of it can be out while the rest waits. Both tables are shown
   * whenever both have rows.
   */
  const isDone = status === "done";
  const partly = isDone && remaining.length > 0;
  const orphaned = totals?.orphanedVariants ?? 0;
  const goingNegative = totals?.goingNegative ?? 0;
  const alreadyNegative = totals?.alreadyNegative ?? 0;

  const blockedReason = blockers.length
    ? `${blockers.length} problem(s) must be fixed before any stock can go out.`
    : remaining.length === 0
    ? "Every variant of this import is already stocked out."
    : !canStockOut
    ? "You do not have permission to stock out."
    : "";

  const disabled =
    Boolean(blockedReason) || working || loading || !outDateValid;
  const noPermission = canStockOut
    ? ""
    : "You do not have permission to undo a stock out.";

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        sx={{ gap: 1, mb: 1 }}
      >
        <Stack
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          sx={{ gap: 1 }}
        >
          <Chip
            size="small"
            color={isDone ? "success" : "default"}
            label={isDone ? "Stocked out" : "Not stocked out"}
          />
          {isDone && stockOutAt && (
            <Chip
              size="small"
              variant="outlined"
              label={`Stocked out on ${moment(stockOutAt).format(
                "DD/MM/YYYY HH:mm"
              )}${stockOutBy?.name ? ` by ${stockOutBy.name}` : ""}`}
            />
          )}
          {records.length > 0 && (
            <Chip
              size="small"
              variant="outlined"
              color="success"
              label={`${totals?.outQuantity ?? 0} pcs out from ${
                records.length
              } variant(s)`}
            />
          )}
          {goingNegative > 0 && !blockers.length && (
            <Chip
              size="small"
              color="error"
              label={`${goingNegative} variant(s) will go below zero`}
            />
          )}
          {alreadyNegative > 0 && (
            <Chip
              size="small"
              color="error"
              variant="outlined"
              label={`${alreadyNegative} variant(s) below zero`}
            />
          )}
          {remaining.length > 0 && !blockers.length && (
            <Chip
              size="small"
              variant="outlined"
              color="warning"
              label={`${
                totals?.remainingQuantity ?? 0
              } pcs still to take out from ${remaining.length} variant(s)`}
            />
          )}
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          {remaining.length > 0 && (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Stock out date"
                inputFormat="dd/MM/yyyy"
                maxDate={new Date()}
                value={outDate}
                onChange={setOutDate}
                disabled={working}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    sx={{ width: 170 }}
                    error={!outDateValid}
                    helperText={
                      outDateValid ? "" : "Pick today or an earlier date"
                    }
                  />
                )}
              />
            </LocalizationProvider>
          )}
          {remaining.length > 0 && (
            <Tooltip title={blockedReason}>
              <span>
                <LoadingButton
                  variant="contained"
                  color="error"
                  size="small"
                  startIcon={<RemoveShoppingCartIcon />}
                  loading={working}
                  disabled={disabled}
                  onClick={confirmOut.onOpen}
                >
                  {partly ? "Stock Out Remaining" : "Stock Out"}
                </LoadingButton>
              </span>
            </Tooltip>
          )}
          {records.length > 0 && (
            <Tooltip title={noPermission}>
              <span>
                <LoadingButton
                  variant="outlined"
                  color="warning"
                  size="small"
                  startIcon={<UndoIcon />}
                  loading={working}
                  disabled={!canStockOut || working}
                  onClick={confirmUndo.onOpen}
                >
                  Undo All
                </LoadingButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 2 }}
      >
        Removes these quantities from app inventory as an <b>Out</b> entry,
        dated the <b>Stock out date</b> you pick (today by default). The size
        chart is what links a label size to a variant
        size, so <b>Free-Size: 36-40</b> on a label can come out of an <b>XL</b>{" "}
        variant when one chart holds both. Edited quantities are what goes out,
        not the original PDF figures.
      </Typography>

      {blockers.length > 0 && (
        <>
          <Alert severity="error" sx={{ mb: 2 }}>
            This import cannot be stocked out yet. Fix the {blockers.length}{" "}
            problem(s) below, then reopen this import.
          </Alert>
          <Table columns={blockerColumns} rows={blockers} />
          <Box sx={{ mb: 3 }} />
        </>
      )}

      {records.length > 0 && (
        <Box sx={{ mb: remaining.length ? 3 : 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
            What was taken out ({records.length})
          </Typography>
          <Table
            columns={variantColumns({
              onRevert: (row) => confirmRevert.stageAction(row),
              revertDisabled: !canStockOut || working,
              stock: "current",
            })}
            rows={recordSort.sorted}
            Order={recordSort.order}
            OrderBy={recordSort.orderBy}
            sortData={recordSort.onSort}
            showTotalInFooter
          />
        </Box>
      )}

      {remaining.length > 0 && (
        <>
          {partly && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <b>{remaining.length} variant(s)</b> of this import are not
              stocked out - either reverted, or resolved to a different variant
              after a mapping fix. Press <b>Stock Out Remaining</b> to take only
              these out.
            </Alert>
          )}
          {/* An entry deleted from the app leaves this import's record
              pointing at nothing, so the variant is listed as still to do. */}
          {orphaned > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <b>{orphaned} variant(s)</b> were stocked out by this import but
              their inventory entries have since been deleted from the app, so
              that stock is no longer out. They are listed below and can be
              taken out again.
            </Alert>
          )}
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
            What will be taken out ({remaining.length})
          </Typography>
          <Table
            columns={variantColumns({ stock: "after" })}
            rows={remainingSort.sorted}
            Order={remainingSort.order}
            OrderBy={remainingSort.orderBy}
            sortData={remainingSort.onSort}
            showTotalInFooter
          />
        </>
      )}

      {!records.length && !remaining.length && !blockers.length && (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          No variant could be resolved for this import.
        </Typography>
      )}

      <DialogConfirm
        isOpen={confirmOut.isOpen}
        onClose={confirmOut.onClose}
        title="Take this stock out of inventory?"
        content={
          <>
            <b>{totals?.remainingQuantity ?? 0}</b> piece(s) will be removed
            from <b>{remaining.length}</b> variant(s), dated{" "}
            <b>{moment(outDate).format("DD/MM/YYYY")}</b>.
            {goingNegative > 0 && (
              <Box
                component="span"
                sx={{ color: "error.main", display: "block", mt: 1 }}
              >
                <b>{goingNegative} variant(s)</b> will go below zero.
              </Box>
            )}{" "}
            Stock is allowed to go negative. You can undo this afterwards, all
            at once or one variant at a time.
          </>
        }
        onConfirm={() => {
          confirmOut.onClose();
          onStockOut(moment(outDate).format("YYYY-MM-DD"));
        }}
        btnLabels={{ cancel: "Cancel", confirm: "Stock Out" }}
        confirmBtnProps={{ color: "error" }}
      />

      <DialogConfirm
        isOpen={confirmUndo.isOpen}
        onClose={confirmUndo.onClose}
        title="Undo this stock out?"
        content={
          <>
            All <b>{records.length}</b> inventory entr
            {records.length === 1 ? "y" : "ies"} still standing from this import
            will be removed and those balances will go back to what they were.
            Entries made by hand are not touched.
          </>
        }
        onConfirm={() => {
          confirmUndo.onClose();
          onUndo();
        }}
        btnLabels={{ cancel: "Cancel", confirm: "Undo All" }}
        confirmBtnProps={{ color: "warning" }}
      />

      <DialogConfirm
        isOpen={confirmRevert.isOpen}
        onClose={confirmRevert.onClose}
        title="Revert this variant?"
        content={
          <>
            <b>{confirmRevert.actionProps?.quantity ?? 0}</b> piece(s) will go
            back to <b>{confirmRevert.actionProps?.variantCode}</b>. The rest of
            this import stays out, and you can take this one out again later.
          </>
        }
        onConfirm={() => {
          const row = confirmRevert.actionProps;
          confirmRevert.onClose();
          onRevert(row);
        }}
        btnLabels={{ cancel: "Cancel", confirm: "Revert" }}
        confirmBtnProps={{ color: "warning" }}
      />
    </Box>
  );
};

export default StockOutPanel;
